import * as awarenessProtocol from 'y-protocols/awareness';

export class AwarenessBatcher {
  private awareness: awarenessProtocol.Awareness;
  private ws: WebSocket | null = null;
  private pendingChanges = new Set<number>();
  private rafId: number | null = null;
  private lastSendTime = 0;
  private readonly minInterval = 16; // ~60fps max
  private isDestroyed = false;

  constructor(awareness: awarenessProtocol.Awareness) {
    this.awareness = awareness;
    this.setupHandler();
  }

  setWebSocket(ws: WebSocket | null): void {
    this.ws = ws;
  }

  private setupHandler(): void {
    this.awareness.on('update', ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: any) => {
      if (this.isDestroyed) return;

      // Skip updates from remote sources
      if (origin === 'websocket') return;

      // Collect changed clients
      const changed = added.concat(updated).concat(removed);
      for (const clientId of changed) {
        this.pendingChanges.add(clientId);
      }

      // Schedule batched send
      this.scheduleSend();
    });
  }

  private scheduleSend(): void {
    if (this.rafId !== null) return; // Already scheduled

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;

      // Throttle to max 60fps
      const now = performance.now();
      if (now - this.lastSendTime < this.minInterval) {
        this.scheduleSend(); // Re-schedule for next frame
        return;
      }

      this.flush();
      this.lastSendTime = now;
    });
  }

  private flush(): void {
    if (this.pendingChanges.size === 0 || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingChanges.clear();
      return;
    }

    try {
      // Encode batched update
      const changedClients = Array.from(this.pendingChanges);
      const update = awarenessProtocol.encodeAwarenessUpdate(this.awareness, changedClients);

      // Send single batched message
      this.ws.send(update);

      // Clear pending
      this.pendingChanges.clear();
    } catch (error) {
      console.error('[AwarenessBatcher] Failed to send update:', error);
      this.pendingChanges.clear();
    }
  }

  destroy(): void {
    this.isDestroyed = true;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Send any pending updates
    this.flush();
  }
}
